export const GREETING_MAX_BYTES = 5 * 1024 * 1024
export const GREETING_MAX_RECORD_SECONDS = 60

export function validateGreetingFile(file) {
  const lower = (file.name || "").toLowerCase()
  if (!lower.endsWith(".mp3") && !lower.endsWith(".wav")) {
    return "Please upload an MP3 or WAV greeting file."
  }
  if (file.size > GREETING_MAX_BYTES) {
    return "Greeting file must be 5 MB or smaller."
  }
  return null
}

export function pickMediaRecorderMimeType() {
  if (typeof MediaRecorder === "undefined") {
    return ""
  }
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ]
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }
  return ""
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i += 1) {
    view.setUint8(offset + i, str.charCodeAt(i))
  }
}

function encodeWav(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels
  const sampleRate = audioBuffer.sampleRate
  const bitsPerSample = 16
  const channelData = []
  for (let i = 0; i < numChannels; i += 1) {
    channelData.push(audioBuffer.getChannelData(i))
  }
  const { length } = audioBuffer
  const buffer = new ArrayBuffer(44 + length * numChannels * 2)
  const view = new DataView(buffer)

  writeString(view, 0, "RIFF")
  view.setUint32(4, 36 + length * numChannels * 2, true)
  writeString(view, 8, "WAVE")
  writeString(view, 12, "fmt ")
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, (sampleRate * numChannels * bitsPerSample) / 8, true)
  view.setUint16(32, (numChannels * bitsPerSample) / 8, true)
  view.setUint16(34, bitsPerSample, true)
  writeString(view, 36, "data")
  view.setUint32(40, length * numChannels * 2, true)

  let offset = 44
  for (let i = 0; i < length; i += 1) {
    for (let ch = 0; ch < numChannels; ch += 1) {
      const sample = Math.max(-1, Math.min(1, channelData[ch][i]))
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true
      )
      offset += 2
    }
  }
  return buffer
}

/** Browser recordings are converted to WAV for Twilio <Play> compatibility. */
export async function convertBlobToWavFile(blob, filename = "greeting.wav") {
  const AudioContext = window.AudioContext || window.webkitAudioContext
  if (!AudioContext) {
    throw new Error("Your browser cannot process audio recordings.")
  }
  const audioContext = new AudioContext()
  try {
    const arrayBuffer = await blob.arrayBuffer()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    const wavBuffer = encodeWav(audioBuffer)
    const wavFile = new File([wavBuffer], filename, { type: "audio/wav" })
    const validationError = validateGreetingFile(wavFile)
    if (validationError) {
      throw new Error(validationError)
    }
    return wavFile
  } finally {
    await audioContext.close()
  }
}
